/* eslint-disable no-prototype-builtins */
import classnames from 'classnames/bind'
import { isEmpty, isNull } from 'lodash-es'
import { useEffect } from 'react'

// Libs
import { useAppDispatch } from '@/redux/hooks'
import { useRouter } from 'next/router'
import { fetchCategoryList } from '@/api/fetchCategoryList'
import { useMallList } from '@/components/shared/MallList/hooks/useMallList'
import { useThemeList } from '@/components/pages/Mall/hooks/useThemeList'
import { setCategoryMenuData, setEventMenuData } from '@/redux/reducers/mall'
import { useRecentLiveStreamingProductList } from './hooks/useRecentLiveStreamingProductList'
import { usePopularProductList } from './hooks/usePopularProductList'
import { fetchPopularProducts } from '@/api/fetchPopularProducts'

// Methods

// Components
import MallList from '@/components/shared/MallList'
import PopularProductList from './components/PopularProductList'
import ThemeHomePage from './components/ThemeHomePage'
import ThemeProductList from '@/components/pages/Mall/components/ThemeProductList'
import RecentLiveStreaming from './components/RecentLiveStreaming'
import NavigationMenu from './components/NavigationMenu'
import Banner from '@/components/pages/Live/components/Banner'
import MallLandingPage from './components/LandingPage'

// Constant
import { ROUTE_PATH } from '@/constants/routePath'
import { THEME_LIST_ID } from '@/constants/theme'
import { GtmItemListName } from '@/constants/gtmEvent'
import { DEFAULT_LOCALE } from '@/constants/defaultLocale'
import { MALL_DEFAULT_PAGE_SIZE } from '@/constants/mall'
import { BANNER_LOCATION } from '@/constants/banner'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

// Interfaces
type CategoryList = {
  categoryId: number
  name: string
}[]
type MallDataSSR = {
  mallDataSSR: {
    categoryMenuSSR: any
    categoryListSSR: CategoryList
    eventNameSSR: string
    popularProductListSSR: any
    categoryListDataSSR?: any
    recentLiveStreamingProductDataSSR?: any
  }
}

const isEven = (value: number) => value % 2 == 0

const Mall = ({ mallDataSSR }: MallDataSSR) => {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const { query } = router
  const { category_id, theme_id = '', event_id = '', category } = query
  const isTestMode = query.hasOwnProperty('testMode') && !isEmpty(query.testMode) ? query.testMode === '1' : false
  const currentLocale = router.locale === 'default' ? DEFAULT_LOCALE : router.locale
  const categoryMenuDataMap: any = {}
  mallDataSSR.categoryMenuSSR?.root?.children?.forEach((menutItem: any) => (categoryMenuDataMap[menutItem.id] = menutItem))

  const { themeListData, isThemeListDataSuccess } = useThemeList()
  const isLandingPage = isEmpty(category_id) && isEmpty(theme_id) && isEmpty(event_id)

  const { categoryListData } = useMallList({
    fetchDataApi: fetchCategoryList,
  })

  const { popularListData } = usePopularProductList({
    fetchDataApi: fetchPopularProducts,
    testMode: isTestMode ? String(query.testMode) : '0',
    popularProductListDataSSR: mallDataSSR.popularProductListSSR,
    defaultPayload: {
      eventId: -1,
      pageSize: MALL_DEFAULT_PAGE_SIZE,
    },
  })

  const { recentLiveStreamingProductData } = useRecentLiveStreamingProductList({
    categoryId: category_id,
    initialData: mallDataSSR.recentLiveStreamingProductDataSSR,
  })

  const hasRecentLiveStreamingProduct = recentLiveStreamingProductData.productsInfo && recentLiveStreamingProductData.productsInfo.length > 0

  const getThemeListDataMap = () => {
    const themeListMap = themeListData.themeHalls.reduce((acc: any, item: any) => {
      acc[item.themeId] = item

      return acc
    }, {})

    return themeListMap
  }

  const getThemeIndex = (themeId: number) => {
    let result = 0

    themeListData.themeHalls.forEach((item: any, index: number) => {
      result = item.themeId === themeId ? index : result
    })

    return result
  }

  useEffect(() => {
    dispatch(setCategoryMenuData({ data: mallDataSSR?.categoryMenuSSR?.root?.children }))
  }, [currentLocale])

  useEffect(() => {
    if (!isNull(mallDataSSR.eventNameSSR)) {
      dispatch(setEventMenuData({ data: mallDataSSR.eventNameSSR }))
    }
  }, [mallDataSSR.eventNameSSR])

  return (
    <div className={cx('mall')}>
      {/* 導覽選單 */}
      {router?.pathname === ROUTE_PATH.MALL && <NavigationMenu />}
      {/* 導覽選單 */}

      {/*  首頁 */}
      {isLandingPage && (
        <MallLandingPage
          categoryListData={categoryListData}
          gtmItemEventParams={{
            itemListName: `${GtmItemListName.CATEGORY}_${categoryMenuDataMap[String(category)]?.name}`,
            itemListId: `category_id=${category}`,
          }}
        />
      )}
      {/*  首頁 */}

      {/*  近期直播 */}
      {category_id && hasRecentLiveStreamingProduct && (
        <div className={cx('recent-live-streaming__wrapper')}>
          <RecentLiveStreaming data={recentLiveStreamingProductData.productsInfo} categoryMenuDataMap={categoryMenuDataMap} />
        </div>
      )}
      {/*  近期直播 */}

      {/* 分類列表 */}
      {category_id && (
        <MallList
          className={cx('mall-list')}
          categoryListData={categoryListData}
          categoryId={category_id as string}
          gtmItemEventParams={{
            itemListName: `${GtmItemListName.CATEGORY}_${categoryMenuDataMap[String(category_id)]?.name}`,
            itemListId: `category_id=${category_id}`,
          }}
        />
      )}

      {/* 主題列表 */}
      {query.hasOwnProperty('theme_id') &&
      theme_id === THEME_LIST_ID &&
      isThemeListDataSuccess &&
      !isEmpty(themeListData?.themeHalls) &&
      themeListData?.themeHalls?.length > 0 ? (
        <ThemeHomePage themeCardList={themeListData.themeHalls} />
      ) : (
        <></>
      )}

      {query.hasOwnProperty('theme_id') &&
        theme_id !== THEME_LIST_ID &&
        !isEmpty(theme_id) &&
        isThemeListDataSuccess &&
        !isEmpty(themeListData?.themeHalls) && (
          <ThemeProductList
            themeId={theme_id}
            themeGoBackData={{ ...getThemeListDataMap()[Number(theme_id)], isEven: isEven(getThemeIndex(Number(theme_id)) + 1) }}
            gtmItemEventParams={{
              itemListName: GtmItemListName.THEME,
              itemListId: `theme_id=${theme_id}`,
            }}
          />
        )}

      {/* 活動專區 */}
      {event_id && (
        <>
          <div className={cx('container')}>
            <Banner className={cx('banner__wrapper')} location={BANNER_LOCATION.EVENT} autoplay={false} />
          </div>

          {!isEmpty(popularListData?.data) && !isEmpty(popularListData?.data?.pages) && popularListData.data.pages.length > 0 && (
            <PopularProductList
              className={cx('popular-list')}
              popularListData={popularListData}
              gtmItemEventParams={{
                itemListName: GtmItemListName.POPULAR_PRODUCT,
                itemListId: `event_id=${event_id}`,
              }}
            />
          )}
        </>
      )}
    </div>
  )
}

export default Mall
